import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const DebitNoteCreate = ({ onTabChange }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    originalInvoiceId: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'BGN',
    items: [
      {
        description: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }
    ],
    notes: '',
    selectedBankAccount: {
      bankName: '',
      swiftCode: '',
      iban: ''
    }
  });

  const [invoices, setInvoices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Fetch invoices for selection
  const fetchInvoices = async () => {
    try {
      const response = await fetch('/api/finance/invoices?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  // Fetch partners - only active partners for debit note creation
  const fetchPartners = async () => {
    try {
      const response = await fetch('/api/finance/partners?activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || data);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      const response = await fetch('/api/finance/companies', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchPartners();
    fetchCompanies();
  }, []);

  // Auto-dismiss success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle original invoice selection
  const handleInvoiceSelection = (invoiceId) => {
    const invoice = invoices.find(inv => inv._id === invoiceId);
    if (invoice) {
      setSelectedInvoice(invoice);
      setFormData(prev => ({
        ...prev,
        originalInvoiceId: invoiceId,
        partnerId: invoice.partnerId._id,
        companyId: invoice.companyId._id,
        selectedBankAccount: invoice.selectedBankAccount || {
          bankName: '',
          swiftCode: '',
          iban: ''
        }
      }));
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    const taxRate = 20; // Fixed 20% tax rate
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;

    return { subtotal, taxAmount, totalAmount };
  };

  const { subtotal, taxAmount, totalAmount } = calculateTotals();

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Calculate item total
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = parseFloat(updatedItems[index].unitPrice) || 0;
      updatedItems[index].total = quantity * unitPrice;
    }

    // Handle description line breaks
    if (field === 'description') {
      const lines = value.split('\n');
      const processedLines = lines.map(line => {
        if (line.length <= 55) return line;
        
        const words = line.split(' ');
        let currentLine = '';
        let result = '';
        
        for (let word of words) {
          if ((currentLine + word).length <= 55) {
            currentLine += (currentLine ? ' ' : '') + word;
          } else {
            if (currentLine) {
              result += (result ? '\n' : '') + currentLine;
              currentLine = word;
            } else {
              result += (result ? '\n' : '') + word.substring(0, 55);
              currentLine = word.substring(55);
            }
          }
        }
        
        if (currentLine) {
          result += (result ? '\n' : '') + currentLine;
        }
        
        return result;
      });
      
      updatedItems[index].description = processedLines.join('\n');
    }

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  // Add new item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          quantity: 1,
          unitPrice: 0,
          total: 0
        }
      ]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Add taxRate to each item
      const itemsWithTax = formData.items.map(item => ({
        ...item,
        taxRate: 20
      }));

      const response = await fetch('/api/finance/debit-notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: JSON.stringify({
          ...formData,
          items: itemsWithTax
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create debit note');
      }

      const debitNote = await response.json();
      setSuccessMessage(t('debitNoteCreate.successMessage'));
      
      // Redirect to debit note details
      setTimeout(() => {
        onTabChange('debit-note-details', { id: debitNote._id });
      }, 1000);

    } catch (error) {
      console.error('Error creating debit note:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatEuro = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('bg-BG');
  };

  return (
    <div>
      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage('')}
            className="text-green-600 hover:text-green-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => onTabChange('debit-notes')}
            className="text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('debitNoteCreate.title')}</h1>
            <p className="text-gray-600">{t('debitNoteCreate.subtitle')}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Original Invoice Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteCreate.originalInvoice.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('debitNoteCreate.originalInvoice.selectLabel')} *
              </label>
              <select
                value={formData.originalInvoiceId}
                onChange={(e) => handleInvoiceSelection(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{t('debitNoteCreate.originalInvoice.placeholder')}</option>
                {invoices.map((invoice) => (
                  <option key={invoice._id} value={invoice._id}>
                    {invoice.invoiceNumber} - {invoice.partnerId?.name} - {formatCurrency(invoice.totalAmount)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedInvoice && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                              <h3 className="text-sm font-medium text-gray-900 mb-2">{t('debitNoteCreate.originalInvoice.detailsTitle')}:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">{t('debitNoteCreate.originalInvoice.invoiceNumber')}:</span>
                  <p className="font-medium">{selectedInvoice.invoiceNumber}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t('debitNoteCreate.originalInvoice.partner')}:</span>
                  <p className="font-medium">{selectedInvoice.partnerId?.name}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t('debitNoteCreate.originalInvoice.issueDate')}:</span>
                  <p className="font-medium">{new Date(selectedInvoice.issueDate).toLocaleDateString('bg-BG')}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t('debitNoteCreate.originalInvoice.totalAmount')}:</span>
                  <p className="font-medium">{formatCurrency(selectedInvoice.totalAmount)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Debit Note Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteCreate.details.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('debitNoteCreate.details.issueDate')} *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatDateForDisplay(formData.issueDate)}
                  onChange={(e) => {
                    // Parse Bulgarian date format (DD.MM.YYYY) to ISO format
                    const parts = e.target.value.split('.');
                    if (parts.length === 3) {
                      const day = parts[0];
                      const month = parts[1];
                      const year = parts[2];
                      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                      if (!isNaN(new Date(isoDate).getTime())) {
                        setFormData(prev => ({ ...prev, issueDate: isoDate }));
                      }
                    }
                  }}
                  placeholder="DD.MM.YYYY"
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'date';
                    input.value = formData.issueDate;
                    input.onchange = (e) => {
                      setFormData(prev => ({ ...prev, issueDate: e.target.value }));
                    };
                    input.click();
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('debitNoteCreate.details.dueDate')} *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatDateForDisplay(formData.dueDate)}
                  onChange={(e) => {
                    // Parse Bulgarian date format (DD.MM.YYYY) to ISO format
                    const parts = e.target.value.split('.');
                    if (parts.length === 3) {
                      const day = parts[0];
                      const month = parts[1];
                      const year = parts[2];
                      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                      if (!isNaN(new Date(isoDate).getTime())) {
                        setFormData(prev => ({ ...prev, dueDate: isoDate }));
                      }
                    }
                  }}
                  placeholder="DD.MM.YYYY"
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'date';
                    input.value = formData.dueDate;
                    input.onchange = (e) => {
                      setFormData(prev => ({ ...prev, dueDate: e.target.value }));
                    };
                    input.click();
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('debitNoteCreate.details.currency')}
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="BGN">BGN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">{t('debitNoteCreate.items.title')}</h2>
            <button
              type="button"
              onClick={addItem}
              className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('debitNoteCreate.items.addItem')}</span>
            </button>
          </div>

          <div className="space-y-4">
            {formData.items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('debitNoteCreate.items.description')} *
                    </label>
                    <textarea
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      required
                      rows={Math.max(1, Math.ceil(item.description.length / 55))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder={t('debitNoteCreate.items.descriptionPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('debitNoteCreate.items.quantity')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('debitNoteCreate.items.unitPrice')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('debitNoteCreate.items.total')}
                    </label>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium text-gray-900">
                          {formatCurrency(item.total)}
                        </div>
                      </div>
                      
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-900 p-2"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteCreate.summary.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* BGN Totals */}
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">BGN</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('debitNoteCreate.summary.subtotal')}:</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('debitNoteCreate.summary.taxAmount')} (20%):</span>
                  <span className="font-medium">{formatCurrency(taxAmount)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-900 font-medium">{t('debitNoteCreate.summary.totalAmount')}:</span>
                  <span className="text-green-600 font-bold">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* EUR Totals */}
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">EUR</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('debitNoteCreate.summary.subtotal')}:</span>
                  <span className="font-medium">{formatEuro(subtotal / 1.95583)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('debitNoteCreate.summary.taxAmount')} (20%):</span>
                  <span className="font-medium">{formatEuro(taxAmount / 1.95583)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-900 font-medium">{t('debitNoteCreate.summary.totalAmount')}:</span>
                  <span className="text-green-600 font-bold">{formatEuro(totalAmount / 1.95583)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('debitNoteCreate.notes.title')}</h2>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={t('debitNoteCreate.notes.placeholder')}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => onTabChange('debit-notes')}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            {t('debitNoteCreate.buttons.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('debitNoteCreate.buttons.creating') : t('debitNoteCreate.buttons.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DebitNoteCreate; 